// Cómo se llama cada segmentación en la empresa del usuario: "área" en vez de
// "gerencia", "centro de trabajo" en vez de "sucursal", "empresa" en vez de
// "sociedad"… Lo decide el backend (GetPalabrasEmpresas, a partir de
// PALABRAS_EMPRESA en .Utils/constantes.js); acá solo se arman las formas que
// piden los textos.
//
// Cambia solo lo que se ve. Los campos GraphQL, las claves del store y las
// columnas de los archivos que se vuelven a cargar (carga masiva de usuarios,
// listas de correo) siguen con sus nombres de siempre.
//
// Los componentes no importan este archivo: leen los términos del mixin global
// de main.js ({{ SUCURSAL.plural }}), que sale del store "terminos". Ver
// claude_docs/plan-terminos-empresa.md.

// Espejo de SEGMENTACIONES del backend (.Utils/constantes.js): el id es lo que
// trae id_segmentacion y lo que usan los catálogos (id_elementos_segmentacion).
export const SEGMENTACIONES = {
  GERENCIA: 1,
  SUCURSAL: 2,
  SINDICATO: 3,
  GENERO: 4,
  NACION: 5,
  TURNO: 6,
  SOCIEDAD: 7,
  CARGO: 8,
};

// Los mismos defaults que PALABRAS_DEFECTO del backend. Se usan mientras llega
// la query, si falla y después de cerrar sesión, para que ningún texto quede
// vacío. Si cambia uno, hay que cambiar el otro.
const DEFECTO = {
  GERENCIA: { singular: "gerencia", plural: "gerencias", genero: "f" },
  SUCURSAL: { singular: "sucursal", plural: "sucursales", genero: "f" },
  SINDICATO: { singular: "sindicato", plural: "sindicatos", genero: "m" },
  GENERO: { singular: "género", plural: "géneros", genero: "m" },
  NACION: { singular: "nacionalidad", plural: "nacionalidades", genero: "f" },
  TURNO: { singular: "turno", plural: "turnos", genero: "m" },
  SOCIEDAD: { singular: "sociedad", plural: "sociedades", genero: "f" },
  CARGO: { singular: "cargo", plural: "cargos", genero: "m" },
};

const conMayuscula = (texto) => texto.charAt(0).toUpperCase() + texto.slice(1);

// Todas las formas que usan los textos, a partir de una fila del backend.
// el / un vienen del backend porque no siempre salen del género: "área" es
// femenino pero lleva "el área" / "un área". los / unos y del / al sí salen
// (del género y de el, respectivamente). Congelado: Vue no lo vuelve reactivo,
// y el store lo reemplaza entero cuando cambia.
const armarTermino = ({ singular, plural, genero, el, un }) => {
  const esFemenino = genero === "f";
  const articulo = el || (esFemenino ? "la" : "el");
  return Object.freeze({
    singular,
    plural,
    Singular: conMayuscula(singular),
    Plural: conMayuscula(plural),
    el: articulo,
    El: conMayuscula(articulo),
    un: un || (esFemenino ? "una" : "un"),
    los: esFemenino ? "las" : "los",
    Los: esFemenino ? "Las" : "Los",
    unos: esFemenino ? "unas" : "unos",
    del: articulo === "el" ? "del" : "de la",
    al: articulo === "el" ? "al" : "a la",
    // Adjetivos y pronombres que concuerdan con el término:
    // segun("Todos", "Todas"), segun("válido", "válida")
    segun: (masculino, femenino) => (esFemenino ? femenino : masculino),
  });
};

// { GERENCIA: término, SUCURSAL: término, … } a partir de la lista del backend
// ([{ id_segmentacion, singular, plural, genero, el, un }]). Siempre devuelve
// las 8 claves: un id que no se conoce se ignora y una segmentación que no
// llega queda con su default.
export const armarTerminos = (lista = []) => {
  const porId = new Map(
    (lista || []).map((fila) => [Number(fila.id_segmentacion), fila])
  );
  return Object.freeze(
    Object.keys(SEGMENTACIONES).reduce((terminos, clave) => {
      terminos[clave] = armarTermino(
        porId.get(SEGMENTACIONES[clave]) || DEFECTO[clave]
      );
      return terminos;
    }, {})
  );
};

export const TERMINOS_DEFECTO = armarTerminos();

// Clave a partir del id (1 → "GERENCIA"), para los nombres que llegan por id
// desde un catálogo. undefined si el id no es una segmentación.
export const claveSegmentacion = (id) =>
  Object.keys(SEGMENTACIONES).find(
    (clave) => SEGMENTACIONES[clave] === Number(id)
  );
